// =============================================
// SUBIR COMPROBANTE DE PAGO (PROPIETARIO)
// Con funciones de EDITAR y ELIMINAR
// =============================================

import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  TextField,
  MenuItem,
  Grid,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  AttachFile as AttachIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  Pending as PendingIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import api from '../../services/api';

const OwnerPaymentUpload = () => {
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Diálogos
  const [openDialog, setOpenDialog] = useState(false);
  const [editingReceipt, setEditingReceipt] = useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [receiptToDelete, setReceiptToDelete] = useState(null);
  
  const [formData, setFormData] = useState({
    amount: '',
    payment_date: new Date().toISOString().split('T')[0],
    payment_method: 'transferencia',
    reference_number: '',
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
    application_type: 'specific',
  });
  const [selectedFile, setSelectedFile] = useState(null);

  useEffect(() => {
    loadReceipts();
  }, []);

  const loadReceipts = async () => {
    try {
      setLoading(true);
      const response = await api.get('/payment-receipts');
      setReceipts(response.data.receipts || []);
    } catch (err) {
      console.error('Error al cargar comprobantes:', err);
    } finally {
      setLoading(false);
    }
  };

  // Abrir diálogo para nuevo comprobante
  const handleOpenDialog = () => {
    setEditingReceipt(null);
    setFormData({
      amount: '',
      payment_date: new Date().toISOString().split('T')[0],
      payment_method: 'transferencia',
      reference_number: '',
      month: new Date().getMonth() + 1,
      year: new Date().getFullYear(),
      application_type: 'specific',
    });
    setSelectedFile(null);
    setOpenDialog(true);
  };

  // Abrir diálogo para editar
  const handleOpenEditDialog = (receipt) => {
    setEditingReceipt(receipt);
    setFormData({
      amount: receipt.amount,
      payment_date: receipt.payment_date.split('T')[0],
      payment_method: receipt.payment_method,
      reference_number: receipt.reference_number || '',
      month: receipt.month || new Date().getMonth() + 1,
      year: receipt.year || new Date().getFullYear(),
      application_type: receipt.application_type || 'specific',
    });
    setSelectedFile(null);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingReceipt(null);
    setSelectedFile(null);
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setError('El archivo no debe superar 5MB');
        return;
      }
      const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];
      if (!validTypes.includes(file.type)) {
        setError('Solo se permiten archivos JPG, PNG o PDF');
        return;
      }
      setSelectedFile(file);
      setError('');
    }
  };

  // Subir o actualizar comprobante
  const handleSubmitReceipt = async () => {
    try {
      if (!formData.amount || !formData.payment_date) {
        setError('Complete todos los campos requeridos');
        return;
      }

      if (!editingReceipt && !selectedFile) {
        setError('Debe seleccionar un archivo');
        return;
      }

      setLoading(true);
      setError('');

      const data = new FormData();
      if (selectedFile) {
        data.append('receipt', selectedFile);
      }
      data.append('amount', formData.amount);
      data.append('payment_date', formData.payment_date);
      data.append('payment_method', formData.payment_method);
      data.append('reference_number', formData.reference_number);
      data.append('application_type', formData.application_type);
      
      if (formData.application_type === 'specific') {
        data.append('month', formData.month);
        data.append('year', formData.year);
      }

      if (editingReceipt) {
        // Actualizar
        await api.put(`/payment-receipts/${editingReceipt.id}`, data, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        setSuccess('Comprobante actualizado exitosamente');
      } else {
        // Crear nuevo
        await api.post('/payment-receipts/upload', data, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        setSuccess('Comprobante subido exitosamente. En revisión por el administrador.');
      }

      handleCloseDialog();
      loadReceipts();

    } catch (err) {
      setError('Error: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  // Confirmar eliminación
  const handleOpenDeleteDialog = (receipt) => {
    setReceiptToDelete(receipt);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setReceiptToDelete(null);
  };

  // Eliminar comprobante
  const handleDeleteReceipt = async () => {
    try {
      setLoading(true);
      setError('');

      await api.delete(`/payment-receipts/${receiptToDelete.id}`);

      setSuccess('Comprobante eliminado exitosamente');
      handleCloseDeleteDialog();
      loadReceipts();

    } catch (err) {
      setError('Error al eliminar: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (id, originalName) => {
    try {
      const response = await api.get(`/payment-receipts/${id}/download`, {
        responseType: 'blob',
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', originalName);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      setError('Error al descargar comprobante');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('es-PA');
  };

  const formatAmount = (amount) => {
    return `$${parseFloat(amount).toFixed(2)}`;
  };

  const getStatusInfo = (status) => {
    const statusMap = {
      pending: { icon: <PendingIcon />, color: 'warning', label: 'Pendiente' },
      verified: { icon: <CheckIcon />, color: 'success', label: 'Verificado' },
      rejected: { icon: <CancelIcon />, color: 'error', label: 'Rechazado' },
    };
    return statusMap[status] || statusMap.pending;
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          Mis Comprobantes de Pago
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Sube tus comprobantes de pago para que el administrador los verifique
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Box sx={{ mb: 3 }}>
        <Button
          variant="contained"
          startIcon={<UploadIcon />}
          onClick={handleOpenDialog}
          size="large"
        >
          Subir Comprobante de Pago
        </Button>
      </Box>

      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>Fecha</strong></TableCell>
                <TableCell><strong>Monto</strong></TableCell>
                <TableCell><strong>Método</strong></TableCell>
                <TableCell><strong>Referencia</strong></TableCell>
                <TableCell><strong>Período</strong></TableCell>
                <TableCell><strong>Estado</strong></TableCell>
                <TableCell><strong>Archivo</strong></TableCell>
                <TableCell align="center"><strong>Acciones</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {receipts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography color="text.secondary">
                      No has subido comprobantes aún
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                receipts.map((receipt) => {
                  const statusInfo = getStatusInfo(receipt.status);
                  const canEdit = receipt.status === 'pending';
                  
                  return (
                    <TableRow key={receipt.id}>
                      <TableCell>{formatDate(receipt.payment_date)}</TableCell>
                      <TableCell>
                        <Typography fontWeight="bold">
                          {formatAmount(receipt.amount)}
                        </Typography>
                      </TableCell>
                      <TableCell>{receipt.payment_method}</TableCell>
                      <TableCell>{receipt.reference_number || '-'}</TableCell>
                      <TableCell>
                        {receipt.application_type === 'distribute' ? (
                          <Chip label="Abono" size="small" color="info" />
                        ) : receipt.month && receipt.year ? (
                          `${receipt.month}/${receipt.year}`
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={statusInfo.icon}
                          label={statusInfo.label}
                          color={statusInfo.color}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={<AttachIcon />}
                          label={receipt.receipt_original_name}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                          <IconButton
                            size="small"
                            onClick={() => handleDownload(receipt.id, receipt.receipt_original_name)}
                            title="Descargar"
                          >
                            <ViewIcon fontSize="small" />
                          </IconButton>
                          {canEdit && (
                            <>
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleOpenEditDialog(receipt)}
                                title="Editar"
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleOpenDeleteDialog(receipt)}
                                title="Eliminar"
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Diálogo Subir/Editar Comprobante */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingReceipt ? 'Editar Comprobante' : 'Subir Comprobante de Pago'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            
            <Alert severity="info">
              {editingReceipt 
                ? 'Actualiza los datos del comprobante. Si no seleccionas un nuevo archivo, se mantendrá el actual.'
                : 'Sube tu comprobante de transferencia o depósito. El administrador lo verificará antes de aplicar el pago.'}
            </Alert>

            <FormControl component="fieldset">
              <FormLabel component="legend">¿Cómo deseas aplicar este pago?</FormLabel>
              <RadioGroup
                value={formData.application_type}
                onChange={(e) => setFormData({ ...formData, application_type: e.target.value })}
              >
                <FormControlLabel 
                  value="specific" 
                  control={<Radio />} 
                  label="Pago específico a un mes" 
                />
                <FormControlLabel 
                  value="distribute" 
                  control={<Radio />} 
                  label="Abono (distribuir desde deudas más antiguas)" 
                />
              </RadioGroup>
            </FormControl>

            <TextField
              required
              fullWidth
              type="number"
              label="Monto Pagado"
              value={formData.amount}
              onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
              InputProps={{
                startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>,
              }}
            />

            <TextField
              required
              fullWidth
              type="date"
              label="Fecha del Pago"
              value={formData.payment_date}
              onChange={(e) => setFormData({ ...formData, payment_date: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />

            <TextField
              select
              required
              fullWidth
              label="Método de Pago"
              value={formData.payment_method}
              onChange={(e) => setFormData({ ...formData, payment_method: e.target.value })}
            >
              <MenuItem value="transferencia">Transferencia Bancaria</MenuItem>
              <MenuItem value="deposito">Depósito</MenuItem>
              <MenuItem value="yappy">Yappy</MenuItem>
              <MenuItem value="otro">Otro</MenuItem>
            </TextField>

            <TextField
              fullWidth
              label="Número de Referencia"
              value={formData.reference_number}
              onChange={(e) => setFormData({ ...formData, reference_number: e.target.value })}
              placeholder="Ej: 123456789"
            />

            {formData.application_type === 'specific' && (
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    required
                    select
                    fullWidth
                    label="Mes"
                    value={formData.month}
                    onChange={(e) => setFormData({ ...formData, month: e.target.value })}
                  >
                    {[1,2,3,4,5,6,7,8,9,10,11,12].map((m) => (
                      <MenuItem key={m} value={m}>{m}</MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={6}>
                  <TextField
                    required
                    fullWidth
                    type="number"
                    label="Año"
                    value={formData.year}
                    onChange={(e) => setFormData({ ...formData, year: e.target.value })}
                  />
                </Grid>
              </Grid>
            )}

            <Box>
              <Button
                variant="outlined"
                component="label"
                fullWidth
                startIcon={<AttachIcon />}
              >
                {selectedFile 
                  ? selectedFile.name 
                  : editingReceipt 
                    ? 'Cambiar Comprobante (Opcional)' 
                    : 'Seleccionar Comprobante (JPG, PNG o PDF)'}
                <input
                  type="file"
                  hidden
                  accept="image/jpeg,image/jpg,image/png,application/pdf"
                  onChange={handleFileSelect}
                />
              </Button>
              {selectedFile && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Tamaño: {(selectedFile.size / 1024).toFixed(2)} KB
                </Typography>
              )}
            </Box>

          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button
            onClick={handleSubmitReceipt}
            variant="contained"
            disabled={loading}
          >
            {loading 
              ? 'Guardando...' 
              : editingReceipt 
                ? 'Actualizar Comprobante' 
                : 'Subir Comprobante'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo Confirmar Eliminación */}
      <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog} maxWidth="sm">
        <DialogTitle>Confirmar Eliminación</DialogTitle>
        <DialogContent>
          {receiptToDelete && (
            <Box>
              <Alert severity="warning" sx={{ mb: 2 }}>
                ¿Estás seguro que deseas eliminar este comprobante?
              </Alert>
              <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                <Typography variant="body2"><strong>Monto:</strong> {formatAmount(receiptToDelete.amount)}</Typography>
                <Typography variant="body2"><strong>Fecha:</strong> {formatDate(receiptToDelete.payment_date)}</Typography>
                <Typography variant="body2"><strong>Método:</strong> {receiptToDelete.payment_method}</Typography>
              </Paper>
              <Typography variant="body2" color="error" sx={{ mt: 2 }}>
                Esta acción no se puede deshacer.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Cancelar</Button>
          <Button
            onClick={handleDeleteReceipt}
            variant="contained"
            color="error"
            disabled={loading}
          >
            {loading ? 'Eliminando...' : 'Eliminar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default OwnerPaymentUpload;
